import {CSSProperties, useEffect, useId, useRef, useState} from 'react'
import {HelpCircle, X} from 'lucide-react'

interface Props {
  titulo?: string
  texto: string
}

export default function Dica({titulo, texto}: Props) {
  const id = useId()
  const [aberto, setAberto] = useState(false)
  const [position, setPosition] = useState<CSSProperties>({})
  const ref = useRef<HTMLSpanElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!aberto) return
    const posicionar = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(300, window.innerWidth - 32)
      const viewport = window.visualViewport
      const viewportTop = viewport?.offsetTop ?? 0
      const viewportHeight = viewport?.height ?? window.innerHeight
      const viewportBottom = viewportTop + viewportHeight
      const top = Math.max(viewportTop + 16, Math.min(rect.bottom + 8, viewportBottom - 200))
      setPosition({
        position: 'fixed',
        width,
        left: Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16)),
        top,
        maxHeight: Math.max(0, viewportBottom - top - 16),
        overflowY: 'auto',
      })
    }
    const outside = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setAberto(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAberto(false)
        trigger.current?.focus()
      }
    }
    posicionar()
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', key)
    window.addEventListener('resize', posicionar)
    window.addEventListener('scroll', posicionar, true)
    window.visualViewport?.addEventListener('resize', posicionar)
    window.visualViewport?.addEventListener('scroll', posicionar)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', key)
      window.removeEventListener('resize', posicionar)
      window.removeEventListener('scroll', posicionar, true)
      window.visualViewport?.removeEventListener('resize', posicionar)
      window.visualViewport?.removeEventListener('scroll', posicionar)
    }
  }, [aberto])

  return (
    <span ref={ref} className='dica' onClick={(event) => event.stopPropagation()}>
      <button
        ref={trigger}
        type='button'
        aria-label={titulo ? `Ajuda: ${titulo}` : 'Ajuda sobre este indicador'}
        aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((value) => !value)}
      >
        <HelpCircle size={16} />
      </button>
      {aberto && (
        <span id={id} className='dica-pop' style={position} role='note'>
          <span className='multi-pop-header'>
            <strong>{titulo || 'Sobre este indicador'}</strong>
            <button
              type='button'
              className='icon-button'
              aria-label='Fechar ajuda'
              onClick={() => {
                setAberto(false)
                trigger.current?.focus()
              }}
            >
              <X size={16} />
            </button>
          </span>
          {texto}
        </span>
      )}
    </span>
  )
}
